import React, { FC } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, RowActionPropsType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import translate from '@translate';
import { ReceivingProductType } from 'src/types/receiving';
import { sizes } from 'src/themes/default';
import DateTimePicker from 'src/components/DateTimePicker';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import { FormErrorsType } from '../../../types/global';

export type EditKeysType = 'RECEIVED' | 'LOT' | 'EXPIRATION_DATE';

const allColumns: TableColumnType[] = [
    {
        key: 'NAME',
        label: 'name',
    },
    {
        key: 'SKU',
        label: 'sku',
    },
    {
        key: 'QUANTITY',
        label: 'quantity',
        type: 'number-filter',
    },
    {
        key: 'RECEIVED',
        label: 'receivedQuantity',
        type: 'number-filter',
        customRender: true,
    },
    {
        key: 'LOT',
        label: 'lot',
        customRender: true,
    },
    {
        key: 'EXPIRATION_DATE',
        label: 'expirationDate',
        type: 'date',
        customRender: true,
    },
];

export interface ProductsTablePropsType {
    data?: ReceivingProductType[];
    onChange: (value: any, error: boolean, errorKey?: string) => void;
    editErrors: FormErrorsType;
}

const ProductsTable: FC<ProductsTablePropsType> = (props) => {
    const getInputName = (id: number, key: string) => `product-${id}-${key}`;

    const handleDeleteRow = (row: TableRowType) => {
        props.onChange(
            props.data?.filter((item) => item.RECEIVINGS_PRODUCTS_ID != row.RECEIVINGS_PRODUCTS_ID),
            false
        );
    };

    const changeValue = (key: EditKeysType, value: any, error: boolean, id: number) => {
        const newData = props.data?.map((product) => {
            if (product.RECEIVINGS_PRODUCTS_ID === id) {
                const updatedProduct = { ...product };
                updatedProduct[key] = value;
                return updatedProduct;
            }
            return product;
        });
        props.onChange(newData, error, getInputName(id, key));
    };

    const customRender: CustomRenderType = (column, row) => {
        const name = getInputName(row.RECEIVINGS_PRODUCTS_ID, column.key);
        switch (column.key) {
            case 'RECEIVED':
                return (
                    <TextFieldDebounced
                        value={row.RECEIVED}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('RECEIVED', value, error, row.RECEIVINGS_PRODUCTS_ID)}
                        name={name}
                        fullWidth
                        required
                        type="numberLocale"
                        min={0}
                    />
                );
            case 'LOT':
                return (
                    <TextFieldDebounced
                        value={row.LOT}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('LOT', value, error, row.RECEIVINGS_PRODUCTS_ID)}
                        name={name}
                        fullWidth
                    />
                );
            case 'EXPIRATION_DATE':
                return (
                    <DateTimePicker
                        value={row.EXPIRATION_DATE}
                        error={props.editErrors[name]}
                        onChange={(value, error) =>
                            changeValue('EXPIRATION_DATE', value, error, row.RECEIVINGS_PRODUCTS_ID)
                        }
                        name={name}
                        fullWidth
                        required
                        type="date"
                    />
                );
            default:
                return null;
        }
    };

    const tableRowActions: RowActionPropsType[] = [
        {
            icon: <HighlightOffIcon />,
            label: translate('receivingDetailConfirm.productTable.action.removeProduct'),
            onClick: handleDeleteRow,
            style: { height: sizes.table_action_with_input_H },
        },
    ];

    const tableRows = props.data || [];

    return (
        <TableSimple
            rowIdKey="RECEIVINGS_PRODUCTS_ID"
            rows={tableRows}
            columsLabelPrefix="receivingDetailConfirm.productTable"
            allColumns={allColumns}
            initSort={{ column: 'RECEIVINGS_PRODUCTS_ID', direction: 'ASC', createIdNumber: true }}
            rowActions={tableRowActions}
            customRender={customRender}
            disableFilter
            showInModal
            maxHeight={'unset'}
        />
    );
};

export default ProductsTable;
